import React, { useState, useMemo } from 'react';
import { Table, TableHead, TableBody, TableRow, TableCell, TableSortLabel, Box, useTheme, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteDeviceDialogBox from './DeleteDeviceDialogBox';

const AllDevicesTable = ({ devicesData = [], selectedCompanyId, isSmallScreen, refetch }) => {
    const theme = useTheme();

    const [order, setOrder] = useState('asc');
    const [orderBy, setOrderBy] = useState('name');
    const [deleteDevice, setDeleteDevice] = useState(null);
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false);

    // Handle sort
    const handleRequestSort = (property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const createSortHandler = (property) => (event) => {
        handleRequestSort(property);
    };

    // Process data
    const filteredDevicesData = useMemo(() => {
        if (!devicesData || !Array.isArray(devicesData)) {
            return [];
        }

        if (!selectedCompanyId) {
            return devicesData;
        } else {
            return devicesData.filter((device) => device.company._id === selectedCompanyId);
        }
    }, [devicesData, selectedCompanyId]);

    const sortedData = useMemo(() => {
        if (!filteredDevicesData || !Array.isArray(filteredDevicesData)) {
            return [];
        }

        const sortedDevices = [...filteredDevicesData].sort((a, b) => {
            if (order === 'asc') {
                return a[orderBy] > b[orderBy] ? 1 : -1;
            } else {
                return b[orderBy] > a[orderBy] ? 1 : -1;
            }
        });
        return sortedDevices;
    }, [filteredDevicesData, orderBy, order]);

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString();
    };

    // Hnadle delete click
    const handleDelete = (device) => {
        setDeleteDevice(device);
        setOpenDeleteDialog(true);
    };

    return (
        <Box
            backgroundColor={theme.palette.background.paper}
            borderRadius="15px"
            sx={{
                overflowX: isSmallScreen ? 'auto' : 'hidden',
                '&::-webkit-scrollbar': {
                    display: 'none',
                },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
                boxShadow: '8px 8px 20px rgba(0, 0, 0, 0.1)',
                mt: '1rem',
            }}
        >
            <Table>
                <TableHead>
                    <TableRow>
                        {['name', 'company', 'type', 'mac', 'firmware', 'ip', 'status', 'site', 'createdAt', 'updatedAt', 'actions'].map((header) => (
                            <TableCell key={header}>
                                <TableSortLabel active={orderBy === header} direction={orderBy === header ? order : 'asc'} onClick={createSortHandler(header)}>
                                    {header.charAt(0).toUpperCase() + header.slice(1).replace(/([A-Z])/g, ' $1')}
                                </TableSortLabel>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {sortedData.map((device) => (
                        <TableRow key={device._id}>
                            <TableCell>{device.name}</TableCell>
                            <TableCell>{device.company?.alternativeName !== 'N/A' ? device.company?.alternativeName : device.company?.name}</TableCell>
                            <TableCell>{device.type}</TableCell>
                            <TableCell>{device.mac}</TableCell>
                            <TableCell>{device.firmware}</TableCell>
                            <TableCell>{device.ip}</TableCell>
                            <TableCell>
                                <span style={{ color: device.status === 'ONLINE' ? 'green' : 'red', fontWeight: 'bold' }}>{device.status}</span>
                            </TableCell>
                            <TableCell>{device.site?.name || device.site}</TableCell>
                            <TableCell>{formatDate(device.createdAt)}</TableCell>
                            <TableCell>{formatDate(device.updatedAt)}</TableCell>
                            <TableCell>
                                <IconButton
                                    onClick={() => handleDelete(device)}
                                    sx={{
                                        '&:focus': { outline: 'none' },
                                        '&:hover': { backgroundColor: 'transparent' },
                                        backgroundColor: 'transparent',
                                    }}
                                >
                                    <DeleteIcon color="error" />
                                </IconButton>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <DeleteDeviceDialogBox open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)} device={deleteDevice} refetch={refetch} />
        </Box>
    );
};

export default AllDevicesTable;
